import React, { useState } from 'react';
import { StanButton } from '@/components';
import { stanNotify } from '@/lib/stanNotify';
import { copyToClipboard } from '@/lib/helpers';
import { useAccountDeletion } from '@/queries';
import { useAuthStore } from '@/stores';
import { useModal } from '@/hooks/useModal';
import { DeleteAccountConfirmationModal } from './modals/DeleteAccountConfirmationModal';
import { DeleteAccountErrorModal } from './modals/DeleteAccountErrorModal';

export const AccountDeletionSection: React.FC = () => {
    const logout = useAuthStore(state => state.logout);
    const deleteModal = useModal();
    const errorModal = useModal();
    const [confirmationText, setConfirmationText] = useState('');
    const [requestCode, setRequestCode] = useState('');
    const [failureReasons, setFailureReasons] = useState<string[]>([]);

    const { mutate: requestDeletion, isPending: deletingAccount } = useAccountDeletion();

    const handleAccountDeletion = () => {
        if (confirmationText.trim().toUpperCase() !== 'DELETE') return;

        requestDeletion(undefined, {
            onSuccess: async () => {
                stanNotify({
                    type: 'success',
                    title: 'Success!',
                    text: 'Your account deletion request has been submitted. You will receive a confirmation email shortly.',
                    duration: 10000,
                });

                // Success case - logout (AuthProvider will handle redirect)
                deleteModal.close();
                setConfirmationText('');
                await logout();
            },
            onError: (error: any) => {
                // Error case - show failure modal with reasons and request code
                setRequestCode(error?.response?.data?.request_id || '');
                setFailureReasons(error?.response?.data?.reasons || []);
                deleteModal.close();
                errorModal.open();
                setConfirmationText('');
            },
        });
    };

    const handleCopyRequestCode = async () => {
        await copyToClipboard(requestCode, {
            successMessage: 'Request code copied to clipboard!',
            errorMessage: 'Could not copy request code to clipboard',
        });
    };

    return (
        <>
            {/* Account Deletion Section */}
            <div className='mt-8'>
                <div className='mb-4'>
                    <h4 className='mb-2'>Request Account Deletion</h4>
                    <p className='text-caption-1-regular mt-1 max-w-sm'>
                        <strong>Are you sure?</strong> Deleting your store permanently erases all of your data and
                        content, and can&apos;t be undone.
                    </p>
                </div>
                <StanButton
                    size='md'
                    name='delete-stan-acct-button'
                    variant='outline'
                    color='destructive'
                    onClick={deleteModal.open}
                >
                    Delete My Account
                </StanButton>
            </div>

            {/* Delete Account Confirmation Modal */}
            <DeleteAccountConfirmationModal
                isOpen={deleteModal.isOpen}
                confirmationText={confirmationText}
                deletingAccount={deletingAccount}
                onClose={() => {
                    deleteModal.close();
                    setConfirmationText('');
                }}
                onConfirm={handleAccountDeletion}
                onConfirmationTextChange={setConfirmationText}
            />

            {/* Delete Account Error Modal */}
            <DeleteAccountErrorModal
                isOpen={errorModal.isOpen}
                failureReasons={failureReasons}
                requestCode={requestCode}
                onClose={errorModal.close}
                onCopyRequestCode={handleCopyRequestCode}
            />
        </>
    );
};
